import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Archive jobs and projects that have been closed for more than a week
 * This function should be called periodically (e.g., daily via a cron job)
 */
export const archiveOldClosedPostings = async () => {
  try {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    // Archive old closed jobs
    const closedJobsQuery = query(
      collection(db, 'jobs'),
      where('status', '==', 'closed'),
      where('closedAt', '<=', oneWeekAgo)
    );

    const closedJobsSnapshot = await getDocs(closedJobsQuery);
    const jobArchivePromises = closedJobsSnapshot.docs.map(jobDoc => 
      updateDoc(doc(db, 'jobs', jobDoc.id), {
        status: 'archived',
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    );

    // Archive old closed projects
    const closedProjectsQuery = query(
      collection(db, 'projects'),
      where('status', '==', 'closed'),
      where('closedAt', '<=', oneWeekAgo)
    );

    const closedProjectsSnapshot = await getDocs(closedProjectsQuery);
    const projectArchivePromises = closedProjectsSnapshot.docs.map(projectDoc => 
      updateDoc(doc(db, 'projects', projectDoc.id), {
        status: 'archived',
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    );

    // Execute all archive operations
    await Promise.all([...jobArchivePromises, ...projectArchivePromises]);

    console.log(`Archived ${jobArchivePromises.length} jobs and ${projectArchivePromises.length} projects`);
    
    return {
      archivedJobs: jobArchivePromises.length,
      archivedProjects: projectArchivePromises.length
    };
  } catch (error) {
    console.error('Error archiving old postings:', error);
    throw error;
  }
};

/**
 * Get jobs/projects by status for the current user
 */
export const getUserPostingsByStatus = async (userId, type = 'jobs', status = 'active') => {
  try {
    const collectionName = type === 'jobs' ? 'jobs' : 'projects';
    const q = query(
      collection(db, collectionName),
      where('postedBy', '==', userId),
      where('status', '==', status)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Error getting user ${type} by status:`, error);
    throw error;
  }
};

/**
 * Check if a posting should show "No longer accepting applications"
 */
export const shouldShowClosedMessage = (posting) => {
  if (posting.status !== 'closed') return false;

  // If no closedAt date, assume it was just closed
  if (!posting.closedAt) return true;

  const closedAt = posting.closedAt?.toDate ? posting.closedAt.toDate() : new Date(posting.closedAt);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  return closedAt > oneWeekAgo; // Show message if closed less than a week ago
};

/**
 * Get visible postings (active + recently closed)
 */
export const getVisiblePostings = async (type = 'jobs', limit = 20) => {
  try {
    const collectionName = type === 'jobs' ? 'jobs' : 'projects';
    
    // Get active postings
    const activeQuery = query(
      collection(db, collectionName),
      where('status', '==', 'active')
    );
    
    // Get recently closed postings (within last week)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    const closedQuery = query(
      collection(db, collectionName),
      where('status', '==', 'closed'),
      where('closedAt', '>', oneWeekAgo)
    );

    const [activeSnapshot, closedSnapshot] = await Promise.all([
      getDocs(activeQuery),
      getDocs(closedQuery)
    ]);

    const activePostings = activeSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const closedPostings = closedSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Combine and sort by posted date
    const allPostings = [...activePostings, ...closedPostings];
    allPostings.sort((a, b) => {
      const dateA = a.postedDate?.toDate ? a.postedDate.toDate() : new Date(a.postedDate);
      const dateB = b.postedDate?.toDate ? b.postedDate.toDate() : new Date(b.postedDate);
      return dateB - dateA;
    });

    return allPostings.slice(0, limit);
  } catch (error) {
    console.error(`Error getting visible ${type}:`, error);
    throw error;
  }
};
